import { OutlinedTextFieldProps } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ReactComponent as CalendarIcon } from "components/Icons/calendar.svg";
import dayjs from "dayjs";
import { Field, getIn, useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import {
  OLDate,
  OLDateWithFinalTimeOfDay,
  OLDateWithInitialTimeOfDay,
} from "utilities/dateFormat";
import { inputStyles } from "./styles";

const useStyles = makeStyles(() =>
  createStyles({
    input: {
      maxWidth: "100%",
      width: ({ fullWidth, width }: { fullWidth?: boolean; width?: string }) =>
        width ? width : fullWidth ? "100%" : "373px",
      ...inputStyles,
    },
  }),
);
type Props = Omit<OutlinedTextFieldProps, "variant">;
interface DateInputProps extends Props {
  format?: string;
  formatstring?: string;
  shouldAddInitialHourOfDay?: boolean;
  shouldAddFinalHourOfDay?: boolean;
  "data-test-id"?: string;
  enableClear?: boolean;
  size?: "medium" | "small";
  onAccept?: () => void;
  width?: string;
}
export function DateInput(props: DateInputProps) {
  const classes = useStyles(props);
  const { message } = useMessage();
  const { setFieldValue } = useFormikContext();
  const {
    shouldAddInitialHourOfDay,
    shouldAddFinalHourOfDay,
    ...preparedProps
  } = props;

  return (
    <Field name={props.name}>
      {({ field, form, meta }: { field: any; form: any; meta: any }) => (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={{ okButtonLabel: message({ id: "save" }) }}
        >
          <DatePicker
            {...field}
            {...preparedProps}
            closeOnSelect={false}
            value={field.value ? dayjs(new Date(field.value)) : null}
            slots={{ openPickerIcon: CalendarIcon }}
            slotProps={{
              openPickerIcon: {
                "data-test-id": `${props["data-test-id"]}-icon`,
              },
              actionBar: {
                actions: [
                  ...(props.enableClear ? ["clear"] : []),
                  "cancel",
                  "accept",
                ],
              },
              toolbar: {
                toolbarTitle: preparedProps.label,
              },
              textField: {
                error: !!(
                  meta.error ||
                  props.error ||
                  getIn(form.status, field.name)
                ),
                helperText:
                  meta.error ||
                  getIn(form.status, field.name) ||
                  props.helperText,
                "data-test-id": props["data-test-id"],
                placeholder: props.placeholder || "",
                size: props.size,
              },
            }}
            disableHighlightToday
            className={`${classes.input} ${props.className}`}
            format={props.format || "DD MMM, YYYY"}
            inputVariant="outlined"
            sx={{
              "& .MuiPickersToolbar-penIconButton": { display: "none" },
            }}
            onChange={(date: any) => {
              if (props.name && date) {
                if (props.shouldAddInitialHourOfDay) {
                  setFieldValue(props.name, OLDateWithInitialTimeOfDay(date));
                } else if (props.shouldAddFinalHourOfDay) {
                  setFieldValue(props.name, OLDateWithFinalTimeOfDay(date));
                } else {
                  setFieldValue(
                    props.name,
                    OLDate(date, {
                      formatString: props.formatstring || "yyyy-MM-dd",
                    }),
                  );
                }
              } else if (props.enableClear && props.name && !date) {
                setFieldValue(props.name, undefined);
              }
            }}
            onAccept={() => {
              props.onAccept && props.onAccept();
            }}
          />
        </LocalizationProvider>
      )}
    </Field>
  );
}
